import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { ServerApiMapper } from '~src/ts/helpers/ServerModelHelper';
import { LineageRelationshipType, RelatedContentType } from '~src/ts/models/ContentLineageRelationshipInfo';
import { CreateLinkedTaskRequest } from '~src/ts/ServerApi/requests/CreateLinkedTaskRequest';
import { DeleteLinkedTasksRequest } from '~src/ts/ServerApi/requests/DeleteLinkedTasksRequest';
import { GetContentLineageRelationshipsRequest } from '~src/ts/ServerApi/requests/GetContentLineageRelationshipsRequest';
import { GetLinkedTaskRequest } from '~src/ts/ServerApi/requests/GetLinkedTaskRequest';
import { ResumeLinkedTasksRequest } from '~src/ts/ServerApi/requests/ResumeLinkedTasksRequest';
import { RunLinkedTasksRequest } from '~src/ts/ServerApi/requests/RunLinkedTasksRequest';
import { UpdateLinkedTaskRequest } from '~src/ts/ServerApi/requests/UpdateLinkedTaskRequest';
import { UpdateLinkedTasksPriorityRequest } from '~src/ts/ServerApi/requests/UpdateLinkedTasksPriorityRequest';
import { UpdateLinkedTasksScheduleRequest } from '~src/ts/ServerApi/requests/UpdateLinkedTasksScheduleRequest';
import { LinkedTaskItem } from '~src/ts/ServerApi/types/LinkedTaskItem';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { displayLinkedTaskToasts } from '../utils/LinkedTaskHelpers';

export class LinkedTasksResource {
  public createLinkedTask(scheduleId: string, linkedTaskItems: LinkedTaskItem[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new CreateLinkedTaskRequest({scheduleId, linkedTaskItems}));
  }

  public updateLinkedTask(id: string, scheduleId: string, linkedTaskItems: LinkedTaskItem[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new UpdateLinkedTaskRequest({id, scheduleId, linkedTaskItems}));
  }

  public getLinkedTask(id: string) {
    return WebHttpClient.sendRequest(new GetLinkedTaskRequest({id}))
      .then((result) => {
        if (result.errors) {
          displayLinkedTaskToasts(result);
        }
        return ServerApiMapper.linkedTask(result);
      });
  }

  public deleteLinkedTasks(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteLinkedTasksRequest({ids: idsBlock})),
      ResultHelper.combineResults
    );
  }

  public resumeLinkedTasks(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new ResumeLinkedTasksRequest({ids: idsBlock})),
      ResultHelper.combineResults
    );
  }

  public runLinkedTasks(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunLinkedTasksRequest({ids: idsBlock})),
      ResultHelper.combineResults
    );
  }

  public updateLinkedTasksSchedule(ids: string[], scheduleId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new UpdateLinkedTasksScheduleRequest({ids: idsBlock, scheduleId: scheduleId})),
      ResultHelper.combineResults
    );
  }

  public setLinkedTasksPriority(ids: string[], priority: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new UpdateLinkedTasksPriorityRequest({ids: idsBlock, priority: priority})),
      ResultHelper.combineResults
    );
  }

  public getContentLineageRelationships(id: string, contentType: RelatedContentType, lineageRelationshipType: LineageRelationshipType) {
    return WebHttpClient.sendRequest(new GetContentLineageRelationshipsRequest({id: id, contentType: contentType, lineageRelationshipType: lineageRelationshipType}))
      .then((result) => {
        if(result.backfillRunning) {
          ToasterService.instance.pop({
            type: ToastType.Warning,
            text: Messages.linkedTaskLineageUnavailable(),
          });
        }
        if(result.errors) {
          return result;
        }
        return ServerApiMapper.contentLineageRelationships(result);
      });
  }
}

export const LinkedTasks = new LinkedTasksResource();
